import type { AccountUploadCategory, ApiResponse, IAccountUpload } from '@bcube/shared';
import { api } from '@/lib/api';

export interface AccountUpload extends Omit<IAccountUpload, 'createdAt' | 'updatedAt'> {
  createdAt: string;
  updatedAt: string;
}

export interface PrepareUploadResponse {
  uploadUrl: string;
  key: string;
  id: string;
  fileUrl: string;
  exists: boolean;
}

export interface ConfirmUploadParams {
  id: string;
  accountId: string;
  name: string;
  key: string;
  category: AccountUploadCategory;
  fieldId?: string;
  extension: string;
  contentType?: string;
  sizeInBytes: number;
  etag?: string;
}

export interface ListUploadsResponse {
  docs: AccountUpload[];
  total: number;
}

export const accountUploadApi = {
  prepareUpload: async (
    accountId: string,
    name: string,
    fileName: string,
    contentType: string,
    category: AccountUploadCategory,
    fieldId?: string,
  ): Promise<PrepareUploadResponse> => {
    const { data } = await api.post<ApiResponse<PrepareUploadResponse>>(
      '/upload/account-upload/prepare',
      {
        accountId,
        name,
        fileName,
        contentType,
        category,
        fieldId,
      },
    );
    return data.data;
  },

  confirmUpload: async (params: ConfirmUploadParams): Promise<{ success: boolean; id: string }> => {
    const { data } = await api.post<ApiResponse<{ id: string }>>(
      '/upload/account-upload/confirm',
      params,
    );
    return { success: true, id: data.data.id };
  },

  list: async (
    accountId: string,
    category?: AccountUploadCategory,
  ): Promise<ListUploadsResponse> => {
    const { data } = await api.get<ApiResponse<ListUploadsResponse>>('/upload/account-upload', {
      params: { accountId, category },
    });
    return data.data;
  },

  getDownloadUrl: async (key: string): Promise<string> => {
    const { data } = await api.get<ApiResponse<{ downloadUrl: string }>>(
      '/upload/account-upload/url',
      {
        params: { key },
      },
    );
    return data.data.downloadUrl;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete('/upload/account-upload', {
      params: { id },
    });
  },
};
